import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';

import styles from './ApplicantFilters.module.scss';
import { Funnel, ChevronDown, ChevronRight, User } from 'lucide-react';
/*
 * Baseline filter state used when no explicit filters are provided
 * or when the user clears all filters from the modal.
 */
const DEFAULT_FILTERS = {
  sortOrder: 'newest',
  selectedLocations: [],
  selectedSkills: [],
};

/*
 * Client ATS applicant filters entrypoint.
 *
 * Renders the Filters modal that controls:
 * - location filters (countries and cities)
 * - skill filters
 */
const ApplicantFilters = ({
  filters,
  onFiltersChange,
  variant,
  availableLocations,
  availableSkills,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters || DEFAULT_FILTERS);
  const [expandedCountries, setExpandedCountries] = useState(new Set());
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [orderedLocations, setOrderedLocations] = useState([]);
  const [orderedSkills, setOrderedSkills] = useState([]);

  /*
   * Compute a stable, ordered list of location groups for this modal session.
   *
   * Selected countries appear first (alphabetical), then unselected countries
   * with the most applicants to fill up the initial 5 options, and the rest
   * are sorted alphabetically. This is evaluated on modal open and the result
   * is kept in state while the modal is open.
   */
  const computeOrderedLocations = (selectedLocationsSource) => {
    if (!availableLocations || availableLocations.length === 0) {
      return [];
    }

    const selectedLocations = selectedLocationsSource || [];

    const isSelectedCountryGroup = (locationGroup) => {
      const { country, cities } = locationGroup;
      if (selectedLocations.includes(country)) {
        return true;
      }
      const cityNames = (cities || []).map((city) => city.name);
      return cityNames.some((cityName) => selectedLocations.includes(cityName));
    };

    const selectedCountries = [];
    const unselectedCountries = [];

    availableLocations.forEach((group) => {
      if (isSelectedCountryGroup(group)) {
        selectedCountries.push(group);
      } else {
        unselectedCountries.push(group);
      }
    });

    selectedCountries.sort((a, b) =>
      a.country.localeCompare(b.country, undefined, { sensitivity: 'base' })
    );

    const unselectedSortedByCount = unselectedCountries.slice().sort((a, b) => {
      const countA = typeof a.count === 'number' ? a.count : 0;
      const countB = typeof b.count === 'number' ? b.count : 0;

      if (countA !== countB) {
        return countB - countA;
      }

      return a.country.localeCompare(b.country, undefined, {
        sensitivity: 'base',
      });
    });

    const maxTop = 5;
    const alreadySelectedCount = selectedCountries.length;

    let topCountCountries = [];
    let remainingCountries = unselectedSortedByCount;

    if (alreadySelectedCount < maxTop) {
      const extraNeeded = maxTop - alreadySelectedCount;
      topCountCountries = unselectedSortedByCount.slice(0, extraNeeded);
      remainingCountries = unselectedSortedByCount.slice(extraNeeded);
    }

    remainingCountries.sort((a, b) =>
      a.country.localeCompare(b.country, undefined, { sensitivity: 'base' })
    );

    return [...selectedCountries, ...topCountCountries, ...remainingCountries];
  };

  /*
   * Compute an ordered list of skills for this modal session.
   *
   * Selected skills appear first (alphabetical), then unselected skills with
   * the highest applicant counts to fill up the initial 5 options, and the
   * remaining skills are sorted alphabetically. Evaluated on modal open and
   * stored in state for the duration of the modal.
   */
  const computeOrderedSkills = (selectedSkillsSource) => {
    if (!availableSkills || availableSkills.length === 0) {
      return [];
    }

    const selectedSkills = selectedSkillsSource || [];

    const selected = [];
    const unselected = [];

    availableSkills.forEach((skill) => {
      if (selectedSkills.includes(skill.name)) {
        selected.push(skill);
      } else {
        unselected.push(skill);
      }
    });

    selected.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    const unselectedSortedByCount = unselected.slice().sort((a, b) => {
      const countA = typeof a.count === 'number' ? a.count : 0;
      const countB = typeof b.count === 'number' ? b.count : 0;

      if (countA !== countB) {
        return countB - countA;
      }

      return a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base',
      });
    });

    const maxTop = 5;
    const alreadySelectedCount = selected.length;

    let topCountSkills = [];
    let remainingSkills = unselectedSortedByCount;

    if (alreadySelectedCount < maxTop) {
      const extraNeeded = maxTop - alreadySelectedCount;
      topCountSkills = unselectedSortedByCount.slice(0, extraNeeded);
      remainingSkills = unselectedSortedByCount.slice(extraNeeded);
    }

    remainingSkills.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    return [...selected, ...topCountSkills, ...remainingSkills];
  };

  /*
   * Toggle a whole country group on or off.
   *
   * When selecting a country, all of its cities are also marked as selected.
   * When deselecting a country, both the country and all of its cities are
   * removed from the selection, and the country group is collapsed.
   */
  const handleToggleCountry = (country, cities) => {
    const currentLocations = draftFilters?.selectedLocations || [];
    const isCountrySelected = currentLocations.includes(country);

    setDraftFilters((prev) => {
      const prevLocations = prev?.selectedLocations || [];

      let newLocations;
      if (isCountrySelected) {
        // Deselect country and all its cities
        newLocations = prevLocations.filter(
          (loc) => loc !== country && !cities.includes(loc)
        );
      } else {
        // Select country (which will show all applicants from that country)
        // Also select all individual cities for UI consistency
        newLocations = prevLocations.filter((loc) => !cities.includes(loc));
        newLocations.push(country);
        cities.forEach((city) => {
          if (!newLocations.includes(city)) {
            newLocations.push(city);
          }
        });
      }

      return {
        ...(prev || DEFAULT_FILTERS),
        selectedLocations: newLocations,
      };
    });
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (isCountrySelected) {
        // When deselecting a country, collapse its cities
        next.delete(country);
      } else if (cities.length > 0) {
        // When selecting a country with cities, expand to show them
        next.add(country);
      }
      return next;
    });
  };

  /*
   * Toggle a single skill name in the draft filter state.
   * Does not affect locations or sort order.
   */
  const handleToggleSkill = (skillName) => {
    const currentSkills = draftFilters?.selectedSkills || [];
    const isSelected = currentSkills.includes(skillName);

    setDraftFilters((prev) => ({
      ...(prev || DEFAULT_FILTERS),
      selectedSkills: isSelected
        ? currentSkills.filter((s) => s !== skillName)
        : [...currentSkills, skillName],
    }));
  };

  /*
   * Toggle a single city within a country.
   *
   * When the last selected city in a country is deselected, the country entry
   * is also removed. When a city is selected, the parent country is ensured
   * to be selected so that country-level filters remain consistent.
   */
  const handleToggleCity = (city, country, allCities) => {
    const isCitySelected = (draftFilters?.selectedLocations || []).includes(
      city
    );

    setDraftFilters((prev) => {
      const currentLocations = prev?.selectedLocations || [];

      let newLocations;
      if (isCitySelected) {
        // Deselect city - keep country and other cities; only remove country if no cities remain
        newLocations = currentLocations.filter((loc) => loc !== city);
        const hasOtherCitiesSelected = allCities.some(
          (c) => c !== city && newLocations.includes(c)
        );
        if (!hasOtherCitiesSelected && newLocations.includes(country)) {
          newLocations = newLocations.filter((loc) => loc !== country);
        }
      } else {
        // Select city and country
        newLocations = [...currentLocations, city];
        if (!newLocations.includes(country)) {
          newLocations.push(country);
        }
      }

      return {
        ...(prev || DEFAULT_FILTERS),
        selectedLocations: newLocations,
      };
    });

    // When selecting a city, expand the country to show cities
    if (!isCitySelected && allCities.length > 0) {
      setExpandedCountries((prev) => new Set(prev).add(country));
    }
  };

  const isHeaderVariant = variant === 'header';

  /*
   * Open the Filters modal and initialise its internal state.
   *
   * - Syncs the draft filters from the latest props
   * - Expands countries that are already selected and have cities
   * - Precomputes orderedLocations and orderedSkills for this session
   * - Resets \"show more\" toggles and then shows the modal
   */
  const handleOpenModal = () => {
    // Sync draft filters with latest incoming filters on open
    const nextFilters = filters || DEFAULT_FILTERS;
    setDraftFilters(nextFilters);
    // Expand countries that are selected and have cities
    const selected = nextFilters?.selectedLocations || [];
    const toExpand = new Set();
    if (availableLocations && availableLocations.length > 0) {
      availableLocations.forEach(({ country, cities }) => {
        if (selected.includes(country) && cities?.length > 0) {
          toExpand.add(country);
        }
      });
    }
    setExpandedCountries(toExpand);
    setOrderedLocations(
      computeOrderedLocations(nextFilters?.selectedLocations || [])
    );
    setOrderedSkills(computeOrderedSkills(nextFilters?.selectedSkills || []));
    setShowAllSkills(false);
    setShowAllLocations(false);
    setShowModal(true);
  };

  /*
   * Toggle the expanded / collapsed visual state for a single country group.
   * Does not change any selected locations, only the way the list is shown.
   */
  const handleToggleCountryExpanded = (country) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(country)) {
        next.delete(country);
      } else {
        next.add(country);
      }
      return next;
    });
  };

  /*
   * Persist the current draft filters to the parent and close the modal.
   */
  const handleSaveFilters = () => {
    if (onFiltersChange) {
      onFiltersChange(draftFilters || DEFAULT_FILTERS);
    }
    setShowModal(false);
  };

  /*
   * Reset all filters back to their defaults, notify the parent,
   * and close the modal.
   */
  const handleClearFilters = () => {
    if (onFiltersChange) {
      onFiltersChange({
        ...DEFAULT_FILTERS,
        selectedLocations: [],
        selectedSkills: [],
      });
    }
    setDraftFilters({
      ...DEFAULT_FILTERS,
      selectedLocations: [],
      selectedSkills: [],
    });
    setShowModal(false);
  };

  /*
   * Calculate how many filter dimensions are currently active
   * (non-default sort order, any locations selected, any skills selected)
   * so we can display a compact badge on the Filters button.
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (filters?.selectedLocations && filters.selectedLocations.length > 0) {
      count++;
    }

    if (filters?.selectedSkills && filters.selectedSkills.length > 0) {
      count++;
    }

    return count;
  }, [filters]);

  return (
    <div
      className={`${styles.filtersWrapper} ${
        isHeaderVariant
          ? styles.filtersWrapperHeader
          : styles.filtersWrapperList
      }`}
    >
      <button
        type="button"
        className={`${styles.filtersButton} ${
          isHeaderVariant
            ? styles.filtersButtonHeader
            : styles.filtersButtonList
        }`}
        onClick={handleOpenModal}
      >
        <span className={styles.filtersButtonIcon}>
          <Funnel width={16} height={16} className={styles.icon} />
        </span>
        <span className={styles.filtersButtonLabel}>Filters</span>
        {activeFilterCount > 0 && (
          <span className={styles.filtersBadge}>{activeFilterCount}</span>
        )}
      </button>

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setShowAllSkills(false);
          setShowAllLocations(false);
        }}
        dialogClassName={styles.filtersModal}
      >
        <Modal.Header closeButton className={styles.filtersModalHeader}>
          <Modal.Title className={styles.filtersModalTitle}>
            Filters
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.filtersModalBody}>
          {availableLocations && availableLocations.length > 0 && (
            <div className={styles.filtersModalContent}>
              <div className={styles.filtersTitle}>Filter by location</div>
              <div className={styles.locationCheckboxes}>
                {(showAllLocations || orderedLocations.length <= 5
                  ? orderedLocations
                  : orderedLocations.slice(0, 5)
                ).map((locationGroup) => {
                  const { country, cities, count } = locationGroup;
                  const currentLocations =
                    draftFilters?.selectedLocations || [];
                  const isCountrySelected = currentLocations.includes(country);
                  const cityNames = cities.map((city) => city.name);
                  const hasSingleCity = cities && cities.length === 1;
                  const singleCityName = hasSingleCity ? cities[0].name : null;
                  const totalCityApplicants = (cities || []).reduce(
                    (acc, city) =>
                      acc + (typeof city.count === 'number' ? city.count : 0),
                    0
                  );
                  const countryCount = typeof count === 'number' ? count : 0;
                  const canInline =
                    hasSingleCity &&
                    singleCityName &&
                    totalCityApplicants === countryCount;
                  const isSingleCitySelected =
                    singleCityName && currentLocations.includes(singleCityName);

                  /*
                   * For multi-city countries, track whether some but not all
                   * cities are selected so we can show an indeterminate state
                   * on the country checkbox.
                   */
                  const selectedCitiesCount = cityNames.filter((cityName) =>
                    currentLocations.includes(cityName)
                  ).length;
                  const isIndeterminate =
                    !isCountrySelected &&
                    selectedCitiesCount > 0 &&
                    selectedCitiesCount < cities.length;

                  const hasCities = cityNames.length > 0;
                  const isExpanded = expandedCountries.has(country);

                  /*
                   * If a country has exactly one city and all of its applicants
                   * are in that city, render a single inline \"Country City\"
                   * checkbox instead of the usual collapsible list. This keeps
                   * the UI compact for simple single-city countries.
                   */
                  if (canInline) {
                    return (
                      <div key={country} className={styles.locationGroup}>
                        <div className={styles.countryRow}>
                          <label className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={
                                isCountrySelected || isSingleCitySelected
                              }
                              onChange={() =>
                                handleToggleCountry(country, [singleCityName])
                              }
                              className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>
                              <strong>{country}</strong>, {singleCityName}
                              {typeof count === 'number' && (
                                <span className={styles.applicantCount}>
                                  {count}
                                  <User
                                    width={12}
                                    height={12}
                                    className={styles.applicantCountIcon}
                                  />
                                </span>
                              )}
                            </span>
                          </label>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={country} className={styles.locationGroup}>
                      {/* Parent country checkbox and collapse toggle */}
                      <div className={styles.countryRow}>
                        <label className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={isCountrySelected}
                            ref={(el) => {
                              if (el) {
                                el.indeterminate = isIndeterminate;
                              }
                            }}
                            onChange={() =>
                              handleToggleCountry(country, cityNames)
                            }
                            className={styles.checkbox}
                          />
                          <span className={styles.checkboxText}>
                            <strong>{country}</strong>
                            {typeof count === 'number' && (
                              <span className={styles.applicantCount}>
                                {count}
                                <User
                                  width={12}
                                  height={12}
                                  className={styles.applicantCountIcon}
                                />
                              </span>
                            )}
                          </span>
                        </label>
                        {hasCities && (
                          <button
                            type="button"
                            className={styles.collapseToggle}
                            onClick={() => handleToggleCountryExpanded(country)}
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? `Collapse ${country} cities`
                                : `Expand ${country} cities`
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown
                                width={18}
                                height={18}
                                className={styles.collapseIcon}
                              />
                            ) : (
                              <ChevronRight
                                width={18}
                                height={18}
                                className={styles.collapseIcon}
                              />
                            )}
                          </button>
                        )}
                      </div>

                      {/* City checkboxes (collapsible) */}
                      {hasCities && isExpanded && (
                        <div className={styles.cityCheckboxes}>
                          {cities.map((city) => {
                            const cityName = city.name;
                            const isCityChecked =
                              currentLocations.includes(cityName);
                            return (
                              <label
                                key={cityName}
                                className={styles.checkboxLabel}
                              >
                                <input
                                  type="checkbox"
                                  checked={isCityChecked}
                                  onChange={() =>
                                    handleToggleCity(
                                      cityName,
                                      country,
                                      cityNames
                                    )
                                  }
                                  className={styles.checkbox}
                                />
                                <span className={styles.checkboxText}>
                                  {cityName}
                                  <span className={styles.applicantCount}>
                                    {city.count}
                                    <User
                                      width={16}
                                      height={16}
                                      className={styles.applicantCountIcon}
                                    />
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {orderedLocations.length > 5 && (
                <button
                  type="button"
                  className={styles.showMoreSkillsButton}
                  onClick={() => setShowAllLocations((prev) => !prev)}
                >
                  {showAllLocations ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

          {availableSkills && availableSkills.length > 0 && (
            <div className={styles.filtersModalContent}>
              <div className={styles.filtersTitle}>Filter by skills</div>
              <div className={styles.skillCheckboxes}>
                {(showAllSkills || orderedSkills.length <= 5
                  ? orderedSkills
                  : orderedSkills.slice(0, 5)
                ).map((skill) => {
                  const skillName = skill.name;
                  const isChecked = (
                    draftFilters?.selectedSkills || []
                  ).includes(skillName);
                  return (
                    <label key={skillName} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleSkill(skillName)}
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxText}>
                        {skillName}
                        <span className={styles.applicantCount}>
                          {skill.count}
                          <User
                            width={12}
                            height={12}
                            className={styles.applicantCountIcon}
                          />
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {orderedSkills.length > 5 && (
                <button
                  type="button"
                  className={styles.showMoreSkillsButton}
                  onClick={() => setShowAllSkills((prev) => !prev)}
                >
                  {showAllSkills ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.filtersModalFooter}>
          <Button
            bsStyle="default"
            onClick={handleClearFilters}
            className={styles.filtersModalButtonSecondary}
          >
            Clear Filters
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleSaveFilters}
            className={styles.filtersModalButtonPrimary}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

ApplicantFilters.propTypes = {
  filters: PropTypes.shape({
    sortOrder: PropTypes.oneOf(['newest', 'oldest']),
    selectedLocations: PropTypes.arrayOf(PropTypes.string),
    selectedSkills: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['header', 'list']),
  availableLocations: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      cities: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          count: PropTypes.number,
        })
      ),
    })
  ),
  availableSkills: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
};

ApplicantFilters.defaultProps = {
  variant: 'header',
  availableLocations: [],
  availableSkills: [],
};

export default ApplicantFilters;
